export type TimerState = "idle" | "running" | "paused" | "finished";

/**
 * Deadline-based countdown.
 *
 * The remaining time is never decremented tick by tick — every read is
 * `deadline - now`. Dropped frames, a backgrounded tab, or a throttled timer
 * therefore cost accuracy in *display refresh* only, never in elapsed time.
 */
export class Countdown {
  private durationMs = 0;
  private restMs = 0;
  private deadline = 0;

  state: TimerState = "idle";

  get duration(): number {
    return this.durationMs;
  }

  /** Set the configured length. Only legal while not running. */
  setDuration(ms: number): void {
    this.durationMs = Math.max(0, Math.round(ms));
    this.restMs = this.durationMs;
    this.state = "idle";
  }

  start(now: number): void {
    if (this.state === "running" || this.durationMs <= 0) return;
    if (this.state === "finished") this.restMs = this.durationMs;
    this.deadline = now + this.restMs;
    this.state = "running";
  }

  pause(now: number): void {
    if (this.state !== "running") return;
    this.restMs = Math.max(0, this.deadline - now);
    this.state = "paused";
  }

  reset(): void {
    this.restMs = this.durationMs;
    this.state = "idle";
  }

  finish(): void {
    this.restMs = 0;
    this.state = "finished";
  }

  remaining(now: number): number {
    if (this.state === "running") return Math.max(0, this.deadline - now);
    return this.restMs;
  }
}
